
import { useState, useEffect } from 'react';

export default function Home() {
  const [currentRole, setCurrentRole] = useState(0);
  const roles = ['Writer', 'Composer', 'Storyteller'];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentRole((prev) => (prev + 1) % roles.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Hero Section */}
      <section
        className="relative min-h-screen flex items-center justify-center bg-cover bg-center bg-gray-900"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('https://static.readdy.ai/image/9e2136080ce659fc7c738e14ae6bca23/1aa39b27202e6a625c5c50c90963dee6.jpeg')`,
        }}
      >
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-6xl md:text-8xl font-bold mb-6 font-serif">
            Kobby Taylor
          </h1>
          <div className="text-3xl md:text-5xl mb-8 h-16 flex items-center justify-center">
            <span className="text-amber-400 transition-all duration-1000 ease-in-out">
              {roles[currentRole]}
            </span>
          </div>
          <p className="text-xl md:text-2xl mb-12 max-w-3xl mx-auto leading-relaxed">
            Multi-award-winning artist bridging theatre, TV, and music through rhythm, storytelling, and cultural innovation
          </p>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-gray-800">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-8 text-amber-400">
                Visionary Artist
              </h2>
              <p className="text-lg mb-6 leading-relaxed">
                Kobby Taylor is a writer, composer, and creative director whose work bridges theatre, TV, and music. His artistic vision explores identity, culture, and the power of unity through rhythm and story.
              </p>
              <p className="text-lg mb-6 leading-relaxed">
                As the musical director of Beats Bus Records, Kobby has composed for theatre, television, and major UK arts organisations, creating works that resonate with emotional depth and cultural significance.
              </p>
              <p className="text-lg leading-relaxed">
                His innovative approach combines Afrobeat, soul, jazz, and cinematic synth textures to create immersive experiences that celebrate heritage while pushing creative boundaries.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://static.readdy.ai/image/9e2136080ce659fc7c738e14ae6bca23/04888b4bc84c579d743d298cb923b3f9.jpeg"
                alt="Kobby Taylor"
                className="w-full h-96 object-cover object-top rounded-lg shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 to-transparent rounded-lg"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 text-amber-400">
            Featured Projects
          </h2>

          {/* The Torch */}
          <div className="mb-20">
            <div className="grid md:grid-cols-2 gap-12 items-center mb-12">
              <div>
                <h3 className="text-3xl md:text-4xl font-bold mb-6 text-amber-400">Incognegro</h3>
                <p className="text-lg mb-6 leading-relaxed">
                  A groundbreaking piece of gig theatre that masterfully blends Afrobeat rhythms, synthesized soundscapes, and powerful storytelling into an unforgettable theatrical experience.
                </p>
              </div>
              <div>
                <img
                  src="https://static.readdy.ai/image/9e2136080ce659fc7c738e14ae6bca23/048901b169ed40f0c6f642cdb8cdfcc1.png"
                  alt="The Torch Theatre"
                  className="w-full h-64 object-cover object-center rounded-lg shadow-xl"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gray-800 p-8 rounded-lg">
                <h4 className="text-2xl font-bold mb-4 text-amber-400">Theatre</h4>
                <p className="text-lg mb-4 leading-relaxed">
                  Following its sold-out debut and endorsement by Afrobeat legend Ebo Taylor, The Torch also known as Incognegro is expanding to touring and festival circuits, bringing its unique fusion of music and narrative to audiences worldwide.
                </p>
                <p className="text-lg leading-relaxed">
                  This innovative production showcases the power of rhythm as a universal language, creating connections across cultures and generations.
                </p>
              </div>

              <div className="bg-gray-800 p-8 rounded-lg">
                <h4 className="text-2xl font-bold mb-4 text-amber-400">TV Adaptation</h4>
                <p className="text-lg mb-4 leading-relaxed">
                  Incognegro reimagines the theatrical experience as a visually rich, character‑driven adaptation exploring themes of family, legacy, and identity in modern Britain.
                </p>
                <p className="text-lg leading-relaxed">
                  This ambitious project promises to bring the emotional depth and cultural resonance of the stage production to television audiences.
                </p>
              </div>
            </div>

            {/* Embedded Video */}
            <div className="mt-12 text-center">
              <h4 className="text-2xl font-bold mb-6 text-amber-400">Watch Incognegro also known as the Torch in Action</h4>
              <div className="flex justify-center">
                <div className="w-full max-w-md">
                  <iframe
                    className="w-full h-96 rounded-lg shadow-xl"
                    src="https://www.youtube.com/embed/AD_it6AFP6Q"
                    title="The Torch Performance"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            </div>
          </div>

          {/* Nostalgia Broken */}
          <div className="mb-20">
            <h3 className="text-3xl md:text-4xl font-bold mb-8 text-center text-amber-400">
              Nostalgia! A Futuristic TV Drama
            </h3>
            <div className="grid md:grid-cols-3 gap-8 items-stretch">
              <div className="md:col-span-2">
                <div className="bg-gray-800 p-8 rounded-lg h-full flex flex-col">
                  <h4 className="text-xl font-bold mb-4 text-amber-400">TV Drama</h4>
                  <p className="text-lg mb-6 leading-relaxed">
                    A new scripted drama project that delves deep into themes of memory, migration, and the emotional cost of progress in contemporary society.
                  </p>
                  <p className="text-lg mb-6 leading-relaxed">
                    With its reflective and cinematic tone, this project combines heartfelt writing with a strong musical identity, creating a unique viewing experience that resonates on multiple emotional levels.
                  </p>
                  <p className="text-lg leading-relaxed flex-grow">
                    This ambitious production explores the intersection of past and future, examining how our memories shape our understanding of progress and identity in an ever‑changing world. The drama weaves together personal narratives with broader social themes, creating a tapestry of human experience that speaks to universal truths about belonging, loss, and hope.
                  </p>
                </div>
              </div>
              <div className="flex items-stretch">
                <img
                  src="https://static.readdy.ai/image/9e2136080ce659fc7c738e14ae6bca23/07dd3cb41d4ce3639a88f423c9db5f6b.png"
                  alt="Nostalgia Broken"
                  className="w-full h-full object-cover rounded-lg shadow-xl"
                />
              </div>
            </div>
          </div>

          {/* God's Plan */}
          <div className="mb-20">
            <h3 className="text-3xl md:text-4xl font-bold mb-8 text-center text-amber-400">
              God's Plan (TV Series)
            </h3>
            <div className="grid md:grid-cols-3 gap-8 items-stretch">
              <div className="md:col-span-2">
                <div className="bg-gray-800 p-8 rounded-lg h-full flex flex-col">
                  <div className="mb-6">
                    <span className="inline-block bg-amber-500 text-black px-4 py-2 rounded-full font-semibold text-sm mb-4">
                      Dramedy • 6 x 30 minutes
                    </span>
                    <p className="text-xl font-bold mb-4 text-amber-400 italic">
                      "You can run from your past — but not your calling."
                    </p>
                  </div>
                  <p className="text-lg mb-6 leading-relaxed">
                    An ex‑boxer with a criminal record becomes Britain's most unlikely pastor — and discovers that God's plan comes with bad lighting, broken chairs, and a whole lot of grace.
                  </p>
                  <p className="text-lg mb-6 leading-relaxed">
                    Heartfelt, funny, and relatable — After Life meets My Name Is Earl (without the swears). A redemption story about faith, family, and second chances, set against the backdrop of northern humour and working‑class resilience.
                  </p>
                  <p className="text-lg leading-relaxed flex-grow">
                    This unique series blends comedy and drama to explore themes of redemption, community, and finding purpose in unexpected places. With its authentic portrayal of working‑class life and innovative musical approach featuring an original grime, gospel, and soul fusion soundtrack (think Kano meets Kirk Franklin), God's Plan offers a fresh perspective on faith and transformation in contemporary Britain.
                  </p>
                </div>
              </div>
              <div className="flex items-stretch">
                <img
                  src="https://static.readdy.ai/image/9e2136080ce659fc7c738e14ae6bca23/7125ec5ccff5d2dcd81788734daf13a5.png"
                  alt="God's Plan TV Series"
                  className="w-full h-full object-cover rounded-lg shadow-xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Music & Composition Section */}
      <section className="py-20 bg-gray-800">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-8 text-amber-400">
              Music &amp; Composition
            </h2>
            <p className="text-xl max-w-3xl mx-auto leading-relaxed">
              Professional composer with credits spanning TV, film, and theatre, creating distinctive soundscapes that blend Afrobeat, soul, jazz, and cinematic synth textures.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-gray-900 p-8 rounded-lg text-center">
              <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-music-2-line text-2xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">Theatre Scores</h3>
              <p className="text-lg leading-relaxed">
                Original compositions for major UK theatre productions, blending traditional and contemporary elements.
              </p>
            </div>

            <div className="bg-gray-900 p-8 rounded-lg text-center">
              <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-tv-line text-2xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">TV &amp; Film</h3>
              <p className="text-lg leading-relaxed">
                Cinematic soundtracks that enhance storytelling through innovative musical narratives and emotional depth.
              </p>
            </div>

            <div className="bg-gray-900 p-8 rounded-lg text-center">
              <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-headphone-line text-2xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">Beats Bus Records</h3>
              <p className="text-lg leading-relaxed">
                Musical director creating cutting‑edge productions that push the boundaries of contemporary sound.
              </p>
            </div>
          </div>

          <div className="bg-gray-900 p-8 rounded-lg">
            <h3 className="text-2xl font-bold mb-6 text-center">Featured Compositions</h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gray-800 p-6 rounded-lg">
                <h4 className="text-xl font-bold mb-3 text-amber-400">Sample Track 1</h4>
                <p className="text-lg mb-4">Synthy underscore for a scene in a play</p>
                <a
                  href="https://on.soundcloud.com/RzITdUUktGp9Wn4bTv"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gray-700 hover:bg-gray-600 h-12 rounded-lg flex items-center justify-center px-4 transition-colors"
                >
                  Listen on SoundCloud
                </a>
              </div>

              <div className="bg-gray-800 p-6 rounded-lg">
                <h4 className="text-xl font-bold mb-3 text-amber-400">Sample Track 2</h4>
                <p className="text-lg mb-4">Ebo Taylor Remix on commission</p>
                <a
                  href="https://open.spotify.com/track/0VYObYIlHn1PhWHFjdXwXr?si=91db51e1c90b4b02"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gray-700 hover:bg-gray-600 h-12 rounded-lg flex items-center justify-center px-4 transition-colors"
                >
                  Listen on Spotify
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* News Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-8 text-amber-400">
              Latest News
            </h2>
            <h3 className="text-2xl md:text-3xl font-bold mb-8">
              Exciting Developments on the Horizon!
            </h3>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-800 p-8 md:p-12 rounded-lg">
              <div className="space-y-6 text-lg leading-relaxed">
                <p>
                  I&#39;m thrilled to share a few big updates. Right now, I&#39;ve got not one, not two, but <span className="text-amber-400 font-semibold">three TV scripts in development</span>. Plus, I&#39;m in talks with <span className="text-amber-400 font-semibold">A national theatre company</span> about bringing <span className="font-semibold">The Torch</span> to life as an outdoor show. Meanwhile, the <span className="text-amber-400 font-semibold">Freedom Festival</span> is considering a theatre version of <span className="font-semibold">Nostalgia</span>, which is super exciting.
                </p>
                <p>
                  On the writing front, I was recently longlisted for the <span className="text-amber-400 font-semibold">Channel 4 Screenwriters&#39; Group</span> and I&#39;m currently also on the longlist for the <span className="text-amber-400 font-semibold">Pentabus writing group</span>—fingers crossed for the final outcome!
                </p>
                <p className="text-amber-400 font-semibold text-xl">
                  Stay tuned for more updates!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-800">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-8 text-amber-400">
            Ready to Collaborate?
          </h2>
          <p className="text-xl mb-12 max-w-2xl mx-auto leading-relaxed">
            Whether you&#39;re looking for original compositions, theatrical collaborations, or TV development partnerships, let&#39;s create something extraordinary together.
          </p>
        </div>
      </section>
    </div>
  );
}
